import React, { useEffect, useState } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { Button, CircularProgress, SvgIcon } from '@mui/material';
import PropTypes from 'prop-types';
import { TextInput } from '../../../Helpers/Inputs';
import { setBasicInfo } from '../../../../sessionStorage';
import Appointment from '../../../../icons/apppointment.svg?react';
import { InputMaskPhone } from "../../../Helpers/Formatters";
import Scheduler from './CustomScheduler';
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined';
import { postApp } from '../../Common/Helpers/PostApp';
import { saveSessionEvent } from "../../../../session";
import { ComplimentaryReview, ComplimentaryReviewBusinessSolutions, DisclosureText } from '../../Common/Appointment/CustomAppointmentData';
import { ConfigurableQuestions, configurableQuestionsSchemaShape } from './ConfigurableQuestions';
import { getActiveQuestionList } from '../../../Helpers/Constants/Constant';
import "./ScheduleForm.scss";

export default function ScheduleForm(props) {

    const [postingApp, setPosting] = useState(false);
    const activeQuestionList = getActiveQuestionList(props.productUrl);

    const formMethods = useForm({
        resolver: yupResolver(ScheduleForm.ValidationSchema(activeQuestionList)),
        mode: 'all',
        reValidateMode: 'all',
        defaultValues: props.formState
    });

    const { handleSubmit, formState: { errors }, setValue } = formMethods;

    // manually call setValue for fields that don't play nice with react-hook-form register
    const handleValueChange = (field, value) => {
        props.onFieldChange(field, value);
    };

    useEffect(() => {
        let email = props.formState.email, firstName = props.formState.firstName, lastName = props.formState.lastName, phone = props.formState.phone;
        if (props.basicInfo && props.basicInfo.firstName !== '') {
            firstName = props.basicInfo.firstName;
        }
        if (props.basicInfo && props.basicInfo.lastName !== '') {
            lastName = props.basicInfo.lastName;
        }
        if (props.basicInfo && props.basicInfo.email !== '') {
            email = props.basicInfo.email;
        }
        if (props.basicInfo.phone) {
            phone = props.basicInfo.phone;
        }
        setValue('firstName', firstName);
        setValue('lastName', lastName);
        setValue('email', email);
        setValue('phone', phone);

        const configurableQuestions = activeQuestionList.length > 0 ? activeQuestionList.reduce((acc, q) => {
            acc[q.question] = props.formState?.configurableQuestions?.[q.question] || '';
            return acc;
        }, {}) : {};

        setValue('configurableQuestions', configurableQuestions);

        props.setMultiField(firstName, lastName, email, configurableQuestions, phone);
    }, []);

    useEffect(() => {
        // Save data into basic info when updated
        if (props.basicInfo.firstName !== props.formState.firstName) {
            props.basicInfo.firstName = props.formState.firstName;
        }
        if (props.basicInfo.lastName !== props.formState.lastName) {
            props.basicInfo.lastName = props.formState.lastName;
        }
        if (props.basicInfo.email !== props.formState.email) {
            props.basicInfo.email = props.formState.email;
        }
        setBasicInfo(props.basicInfo);

    }, [props.formState.firstName, props.formState.lastName, props.formState.email]);

    const submitPostApp = () => {
        setPosting(true);

        postApp(props.basicInfo, props.quoter, null, props.formState, props.quoter.termLength, props.journeyType).then((data) => {
            if (data.success) {
                saveSessionEvent(true);
                props.onNext();
            } else {
                setPosting(false);
            }
        }).finally(() => {
            setPosting(false);
        });
    };

    const handleBack = () => {
        props.setMultiField(props.formState.firstName, props.formState.lastName, props.formState.email, props.formState.configurableQuestions);
        props.onBack();
    }

    return (
        <React.Fragment>
            <Row className="justify-content-center text-center mt-2 hide-class">
                <div className={`icon-container background-color-16`}>
                    <SvgIcon component={Appointment} color='primary' />
                </div>
            </Row>
            <Row className="justify-content-center pt-2 mx-auto text-center appointment-heading pb-1">
                <h4>{props.isBusinessSolutions ? ComplimentaryReviewBusinessSolutions.title : ComplimentaryReview.title}</h4>
            </Row>
            <Row className='mx-auto appointment-text'>
                <p dangerouslySetInnerHTML={{ __html: props.isBusinessSolutions ? ComplimentaryReviewBusinessSolutions.text : ComplimentaryReview.text }}></p>
                <p>{ComplimentaryReview.subText}</p>
            </Row>
            <FormProvider {...formMethods}>
                <Form onSubmit={handleSubmit(submitPostApp)} className='mt-3'>
                    <Row>
                        <Col md={6} className='w-100'>
                            <div className='mb-3'>
                                <TextInput
                                    name="firstName"
                                    id="firstName"
                                    label="First Name"
                                    value={props.formState.firstName}
                                    onChange={(e) => handleValueChange('firstName', e.target.value)}
                                />
                            </div>
                        </Col>
                        <Col md={6} className='w-100'>
                            <div className='mb-3'>
                                <TextInput
                                    name="lastName"
                                    id="lastName"
                                    label="Last Name"
                                    value={props.formState.lastName}
                                    onChange={(e) => handleValueChange('lastName', e.target.value)}
                                />
                            </div>
                        </Col>
                    </Row>
                    <div className='mb-3'>
                        <TextInput
                            name="email"
                            id="email"
                            label="Email"
                            value={props.formState.email}
                            onChange={(e) => handleValueChange('email', e.target.value)}
                        />
                    </div>
                    <div>
                        <div className="mb-1">
                            <TextInput
                                name="phone"
                                id="phone"
                                value={props.formState.phone}
                                onChange={(e) => handleValueChange('phone', e.target.value)}
                                label="Phone Number"
                                InputProps={{
                                    inputComponent: InputMaskPhone,
                                }}
                            />
                        </div>
                        <ConfigurableQuestions
                            formState={props.formState.configurableQuestions}
                            onChange={(value) => handleValueChange('configurableQuestions', value)}
                            questionData={activeQuestionList}
                        />
                        <p className="privacySell d-flex justify-content-center sub-text">
                            {props.isBusinessSolutions ? ComplimentaryReviewBusinessSolutions.note : ComplimentaryReview.note}
                        </p>
                    </div>
                    <Scheduler
                        isBusinessSolutions={props.isBusinessSolutions}
                        errors={errors}
                        setValue={setValue}
                        formState={props.formState}
                        onFieldChange={(field, value) => props.onFieldChange(field, value)} />
                    {postingApp ? (
                        <Row className="mt-4">
                            <Col>
                                <div className="submit-loading-container text-center">
                                    <CircularProgress />
                                </div>
                            </Col>
                        </Row>
                    ) : (
                        <Row className="mt-4">
                            <Col className="text-start pb-3 btn-back">
                                <Button variant="outlined" className='w-100' size="medium" color="primary" onClick={handleBack}>
                                    BACK
                                </Button>
                            </Col>
                            <Col className="text-end pb-3">
                                <Button variant="contained" className='w-100' size="medium" color="primary" type="submit">
                                    SUBMIT
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Form>
            </FormProvider>
            <div className="disclosureText" dangerouslySetInnerHTML={{ __html: DisclosureText }} />
        </React.Fragment>
    );
}

ScheduleForm.propTypes = {
    isBusinessSolutions: PropTypes.bool,
    quoter: PropTypes.object,
    formState: PropTypes.object,
    basicInfo: PropTypes.object,
    onFieldChange: PropTypes.func,
    setMultiField: PropTypes.func,
    onBack: PropTypes.func,
};

ScheduleForm.ValidationSchema = (activeQuestionList) => object().shape({
    firstName: string().required('First Name is required.').matches(/^[a-zA-Z]+$/, 'Invalid First Name'),
    lastName: string().required('Last Name is required.').matches(/^[a-zA-Z]+$/, 'Invalid Last Name'),
    email: string()
        .email('Email Address must be valid.')
        .required('Email Address is required.'),
    phone: string()
        .required('Phone number is required.')
        .matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, 'Phone must be formatted as (123) 456-7890'),
    specificAppt: string()
        .required('Select an appointment option.'),
    timeZone: string().transform(emptyStringToUndefined).when('specificAppt', {
        is: 'schedule',
        then: (schema) => schema.required("Timezone is required."),
        otherwise: (schema) => schema.notRequired(),
    }),
    specificApptDate: string().transform(emptyStringToUndefined).when('specificAppt', {
        is: 'schedule',
        then: (schema) => schema.required("Appointment date is required."),
        otherwise: (schema) => schema.notRequired(),
    }),
    specificApptTime: string()
        .when(['specificAppt','specificApptDate', 'timeZone'],
            ([specificAppt, specificApptDate, timeZone], schema) => {
                if (specificAppt === 'schedule' && specificApptDate && timeZone) {
                    return schema.required('Time of call is required.');
                }
                return schema.notRequired();
            }
        ),
    configurableQuestions: object().shape(configurableQuestionsSchemaShape(activeQuestionList)),
});
